import type { Activity } from "~/v2/api/rest/account";

export const ACTIVITIES_UNAVAILABLE_VALUE = "—";
export const ACTIVITIES_PAGE_SIZE = 20;
export const ACTIVITIES_REQUEST_PAGE_SIZE = ACTIVITIES_PAGE_SIZE + 1;

export type ActivitiesTypeFilter =
  | "all"
  | "trades"
  | "transfers"
  | "dividends"
  | "fees"
  | "interests";

export const ACTIVITIES_TYPE_FILTER_OPTIONS: ReadonlyArray<{
  label: string;
  value: ActivitiesTypeFilter;
}> = [
  { label: "All", value: "all" },
  { label: "Trades", value: "trades" },
  { label: "Transfers", value: "transfers" },
  { label: "Dividends", value: "dividends" },
  { label: "Fees", value: "fees" },
  { label: "Interests", value: "interests" },
];

/** Maps UI buckets to broker activity_types query params (mirrors the v2 page). */
const ACTIVITY_TYPE_QUERY: Record<
  Exclude<ActivitiesTypeFilter, "all">,
  string[]
> = {
  trades: ["FILL"],
  transfers: ["TRANS", "CSD", "CSW", "CSR"],
  dividends: ["DIV"],
  fees: ["FEE", "DIVFEE", "CFEE"],
  interests: ["INT"],
};

export const activityTypesForFilter = (
  filter: ActivitiesTypeFilter,
): string[] | undefined =>
  filter === "all" ? undefined : ACTIVITY_TYPE_QUERY[filter];

export const getActivitiesPage = (
  activities: Activity[] | undefined,
): {
  activities: Activity[];
  nextToken?: string;
} => {
  if (!activities) {
    return { activities: [] };
  }

  const pageActivities = activities.slice(0, ACTIVITIES_PAGE_SIZE);
  const cursorCandidate = pageActivities.at(-1)?.id;
  return {
    activities: pageActivities,
    nextToken:
      activities.length > ACTIVITIES_PAGE_SIZE &&
      typeof cursorCandidate === "string" &&
      cursorCandidate
        ? cursorCandidate
        : undefined,
  };
};

const quantityFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 8,
});

const moneyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Broker `date` (yyyy-mm-dd) is a calendar day, not a midnight instant — keep UTC. */
const dateFormatter = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  timeZone: "UTC",
});

/** Timestamps are market-session events; render in America/New_York. */
const timestampFormatter = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
  timeZone: "America/New_York",
});

const marketDateOnlyFormatter = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  timeZone: "America/New_York",
});

const displayText = (value: unknown): string =>
  typeof value === "string" ? value.trim() : "";

const finiteNumber = (value: unknown): number | undefined => {
  if (
    (typeof value !== "string" && typeof value !== "number") ||
    (typeof value === "string" && value.trim() === "")
  ) {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

export const formatActivityQuantity = (qty: unknown): string => {
  const quantity = finiteNumber(qty);
  return quantity === undefined
    ? ACTIVITIES_UNAVAILABLE_VALUE
    : quantityFormatter.format(quantity);
};

export const formatActivityAmount = (activity: {
  net_amount?: unknown;
  price?: unknown;
  qty?: unknown;
}): string => {
  const net = finiteNumber(activity.net_amount);
  if (net !== undefined) {
    return moneyFormatter.format(net);
  }

  const price = finiteNumber(activity.price);
  const qty = finiteNumber(activity.qty);
  if (price !== undefined && qty !== undefined) {
    return moneyFormatter.format(price * qty);
  }

  return ACTIVITIES_UNAVAILABLE_VALUE;
};

export const formatActivityDate = (activity: {
  date?: unknown;
  transaction_time?: unknown;
}): string => {
  const date = displayText(activity.date);
  if (date) {
    const parsed = Date.parse(date);
    if (!Number.isNaN(parsed)) {
      return dateFormatter.format(parsed);
    }
  }

  const transactionTime = displayText(activity.transaction_time);
  if (transactionTime) {
    const parsed = Date.parse(transactionTime);
    if (!Number.isNaN(parsed)) {
      return timestampFormatter.format(parsed);
    }
  }

  return ACTIVITIES_UNAVAILABLE_VALUE;
};

/**
 * Date-only label with the same timezone contract as {@link formatActivityDate}:
 * broker `date` → UTC calendar day; else America/New_York day of `transaction_time`.
 * Omits clock time so Overview (and similar snapshots) stay honest when not every
 * activity type carries a reliable timestamp.
 */
export const formatActivityDateOnly = (activity: {
  date?: unknown;
  transaction_time?: unknown;
}): string => {
  const date = displayText(activity.date);
  if (date) {
    const parsed = Date.parse(date);
    if (!Number.isNaN(parsed)) {
      return dateFormatter.format(parsed);
    }
  }

  const transactionTime = displayText(activity.transaction_time);
  if (transactionTime) {
    const parsed = Date.parse(transactionTime);
    if (!Number.isNaN(parsed)) {
      return marketDateOnlyFormatter.format(parsed);
    }
  }

  return ACTIVITIES_UNAVAILABLE_VALUE;
};

const capitalize = (value: string): string =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : value;

/** Human-readable description for polymorphic activity rows. */
export const formatActivityDescription = (activity: {
  activity_type?: unknown;
  activity_sub_type?: unknown;
  qty?: unknown;
  side?: unknown;
  symbol?: unknown;
  description?: unknown;
}): string => {
  const activityType = displayText(activity.activity_type);
  const activitySubType = displayText(activity.activity_sub_type);
  const qty = displayText(activity.qty);
  const side = displayText(activity.side);
  const symbol = displayText(activity.symbol);
  const description = displayText(activity.description);

  switch (activityType) {
    case "CSD":
      return "Cash Deposit";
    case "CSW":
      return "Cash Withdrawal";
    case "DIV":
      return symbol ? `Dividend from ${symbol}` : "Dividend";
    case "FILL":
      return (
        [capitalize(side), qty, symbol].filter(Boolean).join(" ") || "Fill"
      );
    case "DIVFEE":
      return "Dividend fee";
    case "CFEE":
      return activitySubType === "FND"
        ? "Crypto fee - Perps Funding"
        : "Crypto fee";
    case "OPEXC":
      return symbol ? `Option Exercise: ${symbol}` : "Option Exercise";
    case "OPTRD":
      return symbol
        ? `Option Trade: ${qty || ACTIVITIES_UNAVAILABLE_VALUE} - ${symbol}`
        : "Option Trade";
    case "OPEXP":
      return symbol ? `Option Expiry: ${symbol}` : "Option Expiry";
    case "OPASN":
      return symbol ? `Option Assignment: ${symbol}` : "Option Assignment";
    case "JNLC":
      return "Journal cash between accounts";
    default:
      return description || ACTIVITIES_UNAVAILABLE_VALUE;
  }
};

export type ActivityRow = Activity & {
  descriptionLabel: string;
  typeLabel: string;
  quantityLabel: string;
  amountLabel: string;
  dateLabel: string;
};

export const toActivityRow = (activity: Activity): ActivityRow => ({
  ...activity,
  descriptionLabel: formatActivityDescription(activity),
  typeLabel:
    displayText(activity.activity_type) || ACTIVITIES_UNAVAILABLE_VALUE,
  quantityLabel: formatActivityQuantity(activity.qty),
  amountLabel: formatActivityAmount(activity),
  dateLabel: formatActivityDate(activity),
});
